<template>
  <v-card class="PublisherDashboardPluginCard" @click="onNavigateToPlugin">
    <div class="image-wrapper">
      <div
        v-if="hasKeyImage"
        class="image"
        :style="{ 'background-image': 'url(' + keyImage + ')' }"
      ></div>
      <div v-else class="image-placeholder">
        <div class="placeholder-content">
          <the-icon icon="logo" class="placeholder-logo" />
          <span class="placeholder-text">No Image Available</span>
        </div>
      </div>
      
      <!-- Status Badge -->
      <div v-if="pluginStatus" class="status-badge" :class="statusBadgeClass">
        {{ pluginStatus }}
      </div>
      
      <!-- Plugin Type Badge -->
      <div class="plugin-type-badge">
        <the-icon icon="logo-small" width="16" height="16" class="mr-1" />
        Plugin
      </div>
    </div>
    
    <div class="pa-5">
      <div class="d-flex justify-space-between items-center">
        <div class="app-name">
          <span v-if="plugin.name">{{ plugin.name }}</span>
          <span v-else class="missing-info">Plugin name required</span>
        </div>
        <v-menu offset-x>
          <template v-slot:activator="{ on, attrs }">
            <v-btn text icon compact v-on="on" v-bind="attrs">
              <the-icon icon="more-vert" />
            </v-btn>
          </template>
          <v-list dark class="the-app-bar__dropdown">
            <v-list-item @click="onNavigateToPlugin">
              <v-list-item-title class="SiteApp__action">
                <span class="SiteApp__icon">
                  <v-icon>visibility</v-icon>
                </span>
                View Plugin
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="editPlugin">
              <v-list-item-title class="SiteApp__action">
                <span class="SiteApp__icon">
                  <the-icon icon="edit" />
                </span>
                Edit Plugin
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      
      <!-- Tags Section -->
      <div class="mt-3 app-tag-wrapper" v-if="hasTags">
        <span class="tag" v-for="tag in pluginTags" :key="tag">{{ tag }}</span>
      </div>
      
      <!-- Description Section -->
      <div class="app-description my-5">
        <div
          v-if="hasDescription"
          v-html="description"
        ></div>
        <div v-else class="missing-info">
          <the-icon icon="warn" class="warning-icon" />
          <span>Description required</span>
        </div>
      </div>
      
      <!-- Footer Section -->
      <div class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <div v-if="hasDeveloperData && plugin.developerData.rating">
            <v-rating
              v-model="plugin.developerData.rating"
              readonly
              background-color="red lighten-3"
              color="red"
              small
              dense
            />
          </div>
          <div v-else class="missing-info small">
            <the-icon icon="warn" class="warning-icon-small" />
            <span>No rating</span>
          </div>
        </div>
        
        <div class="d-flex align-center">
          <the-icon
            class="mr-2"
            color="y1"
            icon="download"
            width="16"
            height="16"
          />
          <span v-if="hasDeveloperData && plugin.developerData.installsCount > 0">
            {{ plugin.developerData.installsCount }}
          </span>
          <span v-else class="missing-info small">0</span>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'PublisherDashboardPluginCard',
  props: {
    plugin: Object,
  },
  computed: {
    keyImage() {
      return this.plugin.developerContent
        ? this.plugin.developerContent.keyImage
        : ''
    },
    hasKeyImage() {
      return this.keyImage && this.keyImage.trim() !== ''
    },
    description() {
      return this.plugin.developerContent
        ? this.plugin.developerContent.description
        : ''
    },
    hasDescription() {
      return this.description && this.description.trim() !== ''
    },
    hasDeveloperData() {
      return this.plugin.developerData && Object.keys(this.plugin.developerData).length > 0
    },
    // Enhanced validation logic for plugin completeness
    pluginCompleteness() {
      const checks = {
        hasName: !!this.plugin.name && this.plugin.name.trim() !== '',
        hasDescription: this.hasDescription,
        hasKeyImage: this.hasKeyImage,
        hasDeveloperData: this.hasDeveloperData,
        hasDeveloperInfo: this.hasDeveloperInfo,
        hasInstallParams: this.hasInstallParams,
        hasCategories: this.hasCategories
      }
      
      const totalChecks = Object.keys(checks).length
      const passedChecks = Object.values(checks).filter(Boolean).length
      
      return {
        checks,
        totalChecks,
        passedChecks,
        percentage: Math.round((passedChecks / totalChecks) * 100)
      }
    },
    pluginStatus() {
      const completeness = this.pluginCompleteness
      
      // Define baseline requirements for submission
      const baselineRequirements = [
        'hasName',
        'hasDescription', 
        'hasDeveloperData',
        'hasDeveloperInfo'
      ]
      
      const baselinePassed = baselineRequirements.every(req => completeness.checks[req])
      
      if (!completeness.checks.hasName) return 'Draft'
      if (!completeness.checks.hasDescription) return 'Incomplete'
      if (!baselinePassed) return 'Pending'
      if (completeness.percentage < 80) return 'Needs Review'
      
      return null // No status badge for complete plugins
    },
    statusBadgeClass() {
      if (!this.plugin.name) return 'status-draft'
      if (!this.hasDescription) return 'status-incomplete'
      if (this.pluginCompleteness.percentage < 80) return 'status-pending'
      return ''
    },
    hasDeveloperInfo() {
      return this.plugin.developerData?.developer?.Name || 
             this.plugin.developerData?.developer?.Email
    },
    hasInstallParams() {
      return this.plugin.developerData?.installParams && 
             this.plugin.developerData.installParams.trim() !== ''
    },
    hasCategories() {
      return this.plugin.developerContent?.categories && 
             this.plugin.developerContent.categories.length > 0
    },
    hasTags() {
      return this.pluginTags && this.pluginTags.length > 0
    },
    pluginTags() {
      // Extract tags from plugin data - you can customize this based on your data structure
      const tags = []
      if (this.plugin.developerContent?.category) {
        tags.push(this.plugin.developerContent.category)
      }
      if (this.plugin.developerContent?.type) {
        tags.push(this.plugin.developerContent.type)
      }
      return tags
    },
  },
  methods: {
    onNavigateToPlugin() {
      this.$router.push({
        name: 'plugin_detail',
        params: { pluginId: this.plugin.slug },
      })
    },
    editPlugin() {
      this.$router.push({
        name: 'plugin_edit',
        params: { pluginId: this.plugin.slug },
      })
    },
  },
}
</script>

<style lang="scss">
$style: PublisherDashboardPluginCard;
.#{$style} {
  // Glass style border with increased corner radius
  background: rgba(37, 36, 55, 0.9) !important;
  border: 1px solid $N5 !important;
  box-shadow: 0px 20px 60px rgba(0, 0, 0, 0.25) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 16px !important;
  transition: all 0.3s ease;
  cursor: pointer;
  
  // Override Vuetify's default card styles
  &.v-card {
    background: rgba(37, 36, 55, 0.9) !important;
    border-radius: 16px !important;
  }
  
  &.theme--dark.v-card {
    background: rgba(37, 36, 55, 0.9) !important;
    color: #fff !important;
  }
  
  &.v-sheet.v-card {
    border-radius: 16px !important;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
    border-color: rgba(73, 109, 219, 0.3) !important;
  }
}

.image-wrapper {
  position: relative;
  margin-bottom: 5px;
  overflow: hidden;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.image {
  width: 100%;
  height: 200px;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.image-placeholder {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $N4 0%, $N7 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(73, 109, 219, 0.1), transparent);
    animation: shimmer 2s infinite;
  }
  
  .placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1;
    position: relative;
  }
  
  .placeholder-logo {
    width: 64px;
    height: 64px;
    margin-bottom: 12px;
    opacity: 0.6;
    filter: grayscale(1);
  }
  
  .placeholder-text {
    font-size: 14px;
    font-weight: 500;
    color: $N2;
    text-align: center;
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.status-badge {
  position: absolute;
  top: 16px;
  left: 16px;
  border-radius: 8px;
  padding: 6px 12px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(8px);
  
  &.status-draft {
    background: rgba($N5, 0.9);
    border: 1px solid rgba($N6, 0.5);
  }
  
  &.status-incomplete {
    background: rgba($Y1, 0.9);
    border: 1px solid rgba($Y1, 0.3);
  }
  
  &.status-pending {
    background: rgba($B1, 0.9);
    border: 1px solid rgba($B1, 0.3);
  }
}

.plugin-type-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  border-radius: 8px;
  padding: 6px 8px;
  background: rgba($B5, 0.9);
  border: 1px solid rgba($B5, 0.3);
  display: flex;
  align-items: center;
  color: white;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(8px);
}

.app-name {
  font-size: 16px;
  font-weight: 600;
  color: $white;
  line-height: 1.4;
}

.app-description {
  min-height: 60px;
  white-space: pre-line;
  color: $N2;
  line-height: 1.5;
  font-size: 14px;
}

.app-tag-wrapper {
  min-height: 32px;
  margin-bottom: 5px;
}

.tag {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 400;
  border-radius: 6px;
  margin-right: 8px;
  background: rgba($N5, 0.8);
  margin-bottom: 5px;
  display: inline-block;
  color: $N2;
  backdrop-filter: blur(4px);
}

.missing-info {
  display: flex;
  align-items: center;
  color: $E1;
  font-size: 14px;
  font-weight: 500;
  
  &.small {
    font-size: 12px;
    color: $Y1;
  }
  
  .warning-icon {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
  
  .warning-icon-small {
    width: 12px;
    height: 12px;
    margin-right: 4px;
  }
}

.SiteApp__icon {
  width: 30px;
  text-align: center;
  .v-icon.v-icon {
    font-size: 20px;
  }
}

.SiteApp__action {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  &.dangerous {
    color: $Y1;
  }
}

.w-full {
  width: 100%;
}
</style>
